<template>
  <div v-if="loading" class="page-center">
    <ag-loading />
  </div>
  <div v-else class="recipe-detail">
    <app-header />
    <div class="recipe-detail-content">
      <ag-breadcrumbs
        :breadcrumbs="breadcrumbs"
        :title="`${isNew ? 'Create' : 'Edit'} Recipe`"
      />
      <h2 v-if="recipe.id"><a @click="routeToView">{{ recipe.name }}</a></h2>
      <ag-form @submit="submit">
        <div class="form-row">
          <!-- Recipe Name/Title -->
          <ag-form-group
            id="name"
            label="Title"
            :validator="$v.recipe.name"
          >
            <ag-input
              id="name"
              :value="recipe.name"
              @change="generateNameKey($event)"
            />
          </ag-form-group>

          <!-- Category (categoryKey used for url) -->
          <ag-form-group
            id="categoryKey"
            label="Category"
            :validator="$v.recipe.categoryKey"
          >
            <category-select
              id="categoryKey"
              :value="recipe.categoryKey"
              @change="setRecipeProp($event.id, 'categoryKey')"
            />
          </ag-form-group>
        </div>

        <div class="form-row">
          <p class="recipe-url" v-if="recipe.categoryKey && recipe.key"><strong>Recipe URL:</strong> thedinnerdebate.com/{{ recipe.categoryKey }}/{{ recipe.key }}</p>
          <!-- AddedBy -->
          <added-by v-if="recipe.userId" :id="recipe.userId" />
        </div>

        <div class="form-row">
          <!-- Description -->
          <ag-form-group
            id="description"
            label="Description"
            :validator="$v.recipe.description"
          >
            <ag-input
              id="description"
              :value="recipe.description"
              @change="setRecipeProp($event, 'description')"
            />
          </ag-form-group>
        </div>

        <ag-form-group id="source-toggle" label="">
          <ag-checkbox
            id="source-toggle"
            label="This is not my recipe"
            :checked="sourceToggle"
            @change="toggleSource"
          />
        </ag-form-group>

        <div v-if="sourceToggle">
          <div class="form-row">
            <!-- Source -->
            <ag-form-group
              id="source"
              label=""
            >
              <label>Source Name <span class="subtle">(Person or Company/Website Name - if other than yourself)</span></label>
              <ag-input
                id="source"
                :value="recipe.source"
                @change="setRecipeProp($event, 'source')"
              />
            </ag-form-group>
            
            <!-- SourceUrl -->
            <ag-form-group
              id="sourceUrl"
              label=""
            >
              <label>Source URL <span class="subtle">(if applicable)</span></label>
              <ag-input
                id="sourceUrl"
                :value="recipe.sourceUrl"
                @change="setRecipeProp($event, 'sourceUrl')"
              />
            </ag-form-group>
          </div>
        </div>
        
        <div class="form-row">
          <!-- Prep Time -->
          <ag-form-group
            id="prep-time"
            label="Prep Time"
            :validator="$v.recipe.prepTime"
          >
            <ag-number
              id="prep-time"
              :value="recipe.prepTime"
              :max-length="3"
              @change="setRecipeProp($event, 'prepTime')"
            />
          </ag-form-group>

          <!-- Cook Time -->
          <ag-form-group
            id="cook-time"
            label="Cook Time"
            :validator="$v.recipe.cookTime"
          >
            <ag-number
              id="cook-time"
              :value="recipe.cookTime"
              :max-length="3"
              @change="setRecipeProp($event, 'cookTime')"
            />
          </ag-form-group>

          <!-- Servings -->
          <ag-form-group
            id="servings"
            label="Servings"
            :validator="$v.recipe.servings"
          >
            <ag-number
              id="servings"
              :value="recipe.servings"
              :max-length="3"
              @change="setRecipeProp($event, 'servings')"
            />
          </ag-form-group>
        </div>

        <ag-form-group
          id="directions"
          label="Directions"
          :validator="$v.recipe.directions"
        >
          <ag-input
            id="directions"
            type="textarea"
            :value="recipe.directions"
            @change="setRecipeProp($event, 'directions')"
          />
        </ag-form-group>

        <!-- ImageUrl -->
        <ag-form-group class="file-uploader" id="image-file" label="">
          <ag-upload
            @change="storeFile"
            label="Upload image"
            :selectedFile="imageFile"
          >
            <template v-slot:icon>
              <ag-icon width="62px" height="62px">
                <upload-cloud-icon />
              </ag-icon>
            </template>
          </ag-upload>
        </ag-form-group>

        <img class="recipe-image" v-if="recipe.imageUrl" :src="recipe.imageUrl" :alt="recipe.name" />

        <!-- Ingredients -->
        <div class="ingredients">
          <label>Ingredients</label>
          <div class="ingredient-sets">
            <div class="ingredient-set" v-for="(ingredientSet, index) in recipe.ingredients" :key="`${ingredientSet.title}-${index}`">
              <Ingredients
                :ingredients="ingredientSet"
                :setIndex="index"
                :displayTitle="recipe.ingredients.length > 1"
                @enter="updateIngredients"
                @title="updateIngredients"
                @remove="removeIngredientSet"
              />
            </div>
            <div class="add-set">
              <ag-button priority="secondary" @click="addNewIngredientSet">Add another set of ingredients</ag-button>
            </div>
          </div>
        </div>

        <!-- Hints -->
        <Hints :hints="recipe.hints" @enter="updateHints" @remove="removeHint" />

        <!-- Tags -->
        <ag-form-group
          id="tag-select"
          label="Tags"
        >
          <div class="tags" v-if="recipe.tags.length">
            <ag-tag
              v-for="(tag, index) in recipe.tags"
              :key="tag.id"
              :id="tag.id"
              :name="tag.name"
              @remove="removeTag(index)"
            />
          </div>
          <tag-select
            id="tag-select"
            :value="recipe.tags"
            @change="addTag"
          />
        </ag-form-group>

        <!-- Related Items -->
        <ag-form-group
          id="relatedItems"
          label="Related Items"
        >
          <div class="related-items">
            <ag-tag
              v-for="(relatedItem, index) in recipe.relatedItems"
              :key="relatedItem.id"
              :id="relatedItem.id"
              :name="relatedItem.name"
              @remove="removeRelatedItem(index)"
            />
          </div>
          <recipe-select
            id="relatedItems"
            @change="addRelatedItem"
          />
        </ag-form-group>

        <template v-slot:footer>
          <ag-button priority="secondary" @click="cancel">Cancel</ag-button>
          <ag-button type="submit" :disabled="$v.$invalid || !$v.$anyDirty">
            {{ isNew ? "Create" : "Save" }}
          </ag-button>
        </template>
      </ag-form>
      <div class="delete-recipe" v-if="!isNew">
          <ag-button priority="warning" @click="deleteRecipe">Delete Recipe</ag-button>
      </div>
    </div>
  </div>
</template>

<script>
  import AppHeader from "@/components/Header";
  import RecipeService from "../../services/recipe-service";
  import {
    required,
    integer,
    maxLength
  } from "vuelidate/lib/validators";
  import CategorySelect from "./components/CategorySelect";
  import TagSelect from "./components/TagSelect";
  import RecipeSelect from "./components/RecipeSelect";
  import Hints from "./components/Hints";
  import Ingredients from "./components/Ingredients";
  import AddedBy from "./components/AddedBy";
  import { UploadCloudIcon } from "vue-feather-icons";
  import { pageTitleBase } from "../../router";

  export default {
    name: "RecipeDetail",
    data: () => ({
      loading: true,
      recipe: {
        id: null,
        name: null,
        key: null,
        description: null,
        categoryKey: null,
        source: null,
        sourceUrl: null,
        prepTime: null,
        cookTime: null,
        directions: null,
        servings: null,
        hints: [],
        ingredients: [{
          title: null,
          list: []
        }],
        userId: null,
        relatedItems: [],
        imageUrl: null,
        tags: [],
      },
      imageFile: null,
      sourceToggle: false,
      breadcrumbs: ["Recipes"]
    }),
    async mounted() {
      const { categoryKey, key = null } = this.$route.params;
      if(categoryKey !== 'new') {
        try {
          const result = await this.$service.recipe.findByKeys(categoryKey, key);

          this.recipe = {
            ...this.recipe,
            ...result
          };

          if (this.recipe.source || this.recipe.sourceUrl) {
            this.sourceToggle = true;
          }

          if(this.recipe.name) {
            document.title = `Edit | ${this.recipe.name} | ${pageTitleBase}`;
          }
        } catch (e) {
          console.error(`Error loading recipe by keys [${categoryKey}, ${key}]`, e);
          // TODO Display reason in global error component
        }

        try {
          const tags = await this.$service.recipe.findTagsByRecipeId(this.recipe.id);
          this.recipe.tags = tags;
        } catch (e) {
          console.error(`Error loading tags for recipe with id [${this.recipe.id}]`, e);
        }

        try {
          const relatedItems = await this.$service.recipe.findRelatedRecipesByRecipeId(this.recipe.id);
          this.recipe.relatedItems = relatedItems;
        } catch (e) {
          console.error(`Error loading related recipes for recipe id [${this.recipe.id}]`, e);
        }
      }

      if(this.recipe.ingredients === null) {
        this.recipe.ingredients = [{
          title: null,
          list: []
        }];
      }

      this.loading = false;
    },
    computed: {
      isNew() {
        return this.$route.params.categoryKey === "new";
      }
    },
    methods: {
      routeToView() {
        this.$router.push({
          name: "RecipeView",
          params: { categoryKey: this.recipe.categoryKey, key: this.recipe.key }
        });
      },
      async submit() {
        // Check whether the form is valid
        this.$v.$touch();
        if (this.$v.$invalid) {
          console.error("Recipe not valid", this.recipe);
          return;
        }

        if (this.imageFile) {
          try {
            const fileUrl = await this.uploadFile();
            this.setRecipeProp(fileUrl, "imageUrl");
          } catch (e) {
            // TODO
            console.error("Error uploading file", e);
          }
        }

        if(this.recipe.ingredients.length) {
          // filter out any ingredient sets that have no ingredients
          const trimIngredients = this.recipe.ingredients.filter(set => {
            return set.list && set.list.length;
          });

          this.recipe.ingredients = trimIngredients.length ? trimIngredients : null;
        }

        const recipe = this.recipe;

        try {
          const savedRecipe = await RecipeService.save({
            ...recipe
          });

          if(savedRecipe.data && savedRecipe.data.categoryKey && savedRecipe.data.key) {
            this.$router.push({
              name: "RecipeView",
              params: { categoryKey: savedRecipe.data.categoryKey, key: savedRecipe.data.key }
            });
          } else {
            this.$router.back();
          }
        } catch (e) {
          console.error("Error saving recipe: ", e);
        }
      },
      async deleteRecipe() {
        try {
          await RecipeService.remove(this.recipe.id);
          this.$router.push({ name: "Recipes" });
        } catch (e) {
          console.error("Error deleting recipe: ", e);
        }
      },
      cancel() {
        this.$router.back();
      },
      generateNameKey(name) {
        const key = name.replace(/\W+/g, '-').toLowerCase();
        this.setRecipeProp(name, 'name');
        this.setRecipeProp(key, 'key');
      },
      setRecipeProp(value, property) {
        this.recipe[property] = value;
        const validationProp = this.$v["recipe"][property];
        if (validationProp) {
          validationProp.$touch();
        }
        // Check whether the form is valid
        this.$v.$touch();
      },
      toggleSource() {
        this.sourceToggle = !this.sourceToggle;
      },
      updateHints(hint) {
        this.recipe.hints.push(hint);
        this.$v.$touch();
      },
      removeHint(index) {
        this.recipe.hints.splice(index, 1);
        this.$v.$touch();
      },
      updateIngredients(ingredientSet, setIndex) {
        this.recipe.ingredients.splice(setIndex, 1, ingredientSet);
        this.$v.$touch();
      },
      addNewIngredientSet() {
        this.recipe.ingredients.push({
          title: null,
          list: []
        });
        this.$v.$touch();
      },
      removeIngredientSet(setIndex) {
        this.recipe.ingredients.splice(setIndex, 1);
        this.$v.$touch();
      },
      addRelatedItem(item) {
        if(!this.recipe.relatedItems.some(related => related.id === item.id)) {
          this.recipe.relatedItems.push({
            id: item.id,
            name: item.label
          });
          this.$v.$touch();
        }
      },
      removeRelatedItem(index) {
        this.recipe.relatedItems.splice(index, 1);
        this.$v.$touch();
      },
      storeFile(file) {
        this.imageFile = file;
        this.$v.$touch();
      },
      async uploadFile() {
        if (this.imageFile) {
          const formData = new FormData();
          formData.append("file", this.imageFile);
          const result = await this.$service.recipe.upload(formData);
          return result.data.fileUrl;
        }
      },
      addTag(tag) {
        if(!this.recipe.tags.some(item => item.id === tag.id)) {
          this.recipe.tags.push({
            id: tag.id,
            name: tag.label
          });
          this.$v.$touch();
        }
      },
      removeTag(index) {
        this.recipe.tags.splice(index, 1);
        this.$v.$touch();
      }
    },
    validations: {
      recipe: {
        name: {
          required
        },
        key: {
          required
        },
        categoryKey: {
          required
        },
        prepTime: {
          integer,
          maxLength: maxLength(3)
        },
        cookTime: {
          integer,
          maxLength: maxLength(3)
        },
        servings: {
          integer,
          maxLength: maxLength(3)
        },
      }
    },
    components: { AppHeader, CategorySelect, TagSelect, RecipeSelect, Hints, Ingredients, AddedBy, UploadCloudIcon }
  };
</script>

<style scoped lang="scss">
  .recipe-detail-content {
    padding: $gutter;
  }

  .recipe-url {
    strong {
      display: block;
      text-transform: uppercase;
      font-size: 12px;
      line-height: 1em;
      margin-bottom: 4px;
    }
  }

  a:hover {
    cursor: pointer;
  }

  .subtle {
    font-size: smaller;
    color: $color-gray;
  }

  .file-uploader {
    svg {
      stroke: $color-secondary;
    }
  }

  .recipe-image {
    margin-bottom: $gutter;
  }

  .ingredients {
    .ingredient-sets {
      display: flex;
      flex-wrap: wrap;
      margin-top: 8px;

      .ingredient-set,
      .add-set {
        flex: 0 1 49%;
        box-sizing: border-box;
        margin-bottom: 30px;
        background: #fafafa;
        border: $border;
        border-radius: $radius;
        padding: $gutter;

        &:nth-child(odd) {
          margin-right: 2%;
        }

        @media (max-width: $media-medium) {
          flex: 0 1 100%;
          margin-right: 0;

          &:nth-child(odd) {
            margin-right: 0;
          }

        }
      }

      .add-set {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  .tag {
    margin-right: 10px;
    margin-bottom: $gutter;
  }

  label {
    font-weight: $font-semibold;
    padding-bottom: 5px;
  }

  .delete-recipe {
    padding: 40px 0 0 0;
    text-align: right;
  }
</style>
